import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { serverStore } from '../../stores/server.store';
import { Button } from '../design/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../design/Card';
import { apiFetch } from '../../utils/api';
import toast from 'react-hot-toast';
import { Download, Boxes, Info } from 'lucide-react';
import { FunLoader } from '../common/FunLoader';

/**
 * Install / update panel for a Minecraft Bedrock server. Bedrock ships as a
 * single native binary and has no mods/plugins, so this is intentionally simple.
 */
const BedrockTab = observer(() => {
    const activeServer = serverStore.activeServer;
    const [latest, setLatest] = useState<string>('');
    const [installing, setInstalling] = useState(false);

    useEffect(() => {
        apiFetch('/api/proxies/bedrock/version')
            .then(async (r) => { if (r.ok) { const d = await r.json(); setLatest(d.version || ''); } })
            .catch(() => {});
    }, []);

    const handleInstall = async () => {
        if (!activeServer) return;
        if (!confirm(`Bedrock ${latest || ''} installieren/aktualisieren?\nWelt & Konfiguration bleiben erhalten.`)) return;
        setInstalling(true);
        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/bedrock/install`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ restart: true }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Installation fehlgeschlagen');
            toast.success(data.message || 'Bedrock installiert');
        } catch (e: any) {
            toast.error(e.message || 'Installation fehlgeschlagen');
        } finally {
            setInstalling(false);
        }
    };

    if (!activeServer) return null;

    return (
        <div className="max-w-xl">
            <Card className="bg-ink border-line-strong text-white" noPadding>
                <CardHeader>
                    <CardTitle className="text-lg flex items-center">
                        <Boxes className="mr-2 text-ember" />
                        Bedrock Server
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    <div className="flex items-center justify-between rounded-lg bg-surface-2 border border-line p-4">
                        <div>
                            <div className="text-xs uppercase text-fg-muted font-bold">Aktuellste Version</div>
                            <div className="text-xl font-mono text-ember">{latest || '…'}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs uppercase text-fg-muted font-bold">Installiert</div>
                            <div className="text-xl font-mono text-white">{activeServer.version || '—'}</div>
                        </div>
                    </div>

                    <Button className="w-full bg-ember hover:bg-ember-2" onClick={handleInstall} disabled={installing}>
                        {installing ? <FunLoader size="sm" showProgress={false} /> : (
                            <><Download className="mr-2" size={16} /> Bedrock Installieren / Aktualisieren</>
                        )}
                    </Button>

                    <p className="text-xs text-fg-muted flex items-start gap-2">
                        <Info size={14} className="mt-0.5 shrink-0 text-fg-muted" />
                        Bedrock läuft als natives Binary (kein Java). Mods/Plugins gibt es hier nicht; die
                        Steuerung erfolgt über die Konsole. Welt &amp; Konfiguration bleiben bei Updates erhalten.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
});

export default BedrockTab;
